import { useParams } from "react-router-dom";
import { useState } from "react";
import { getGuardrails, getGuardrailsMarkdown, listGuardrails } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { useAsync } from "@/lib/useAsync";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { BrowseLoading, BrowsePage } from "@/components/BrowsePage";
import { CapabilityLink, CapabilityLinks, StandardLink } from "@/components/RegistryLinks";
import { MapFreshness } from "@/components/MapFreshness";
import { ago } from "@/lib/time";

/** A delivery brief is a map a Staff+ engineer has signed off, reconciled with its review:
 * what a delivery team acts on. Only signed-off maps appear here — an unreviewed map is a
 * model's suggestion, and listing it would dress a guess in a decision's clothes. See
 * docs/user-guide.md#page-4-guardrails (the page's former name).
 *
 * In the shared browse layout (components/BrowsePage.tsx): a card per brief, then one brief at
 * `/briefs/<map id>` (old `/guardrails` links redirect). Read-only — corrections go through
 * Review. */
export function DeliveryBriefsPage() {
  const list = useAsync(listGuardrails, []);
  const { mapId } = useParams();

  if (list.status !== "ready")
    return (
      <BrowseLoading title="Delivery briefs" noun="briefs" error={list.status === "error" ? list.message : undefined} />
    );

  const maps = list.data.maps;
  return (
    <BrowsePage
      title="Delivery briefs"
      meta={`${maps.length} signed off`}
      items={maps}
      itemKey={(m) => m.id}
      itemPath={(m) => `/briefs/${encodeURIComponent(m.id)}`}
      selectedKey={mapId ?? null}
      basePath="/briefs"
      searchable={(m) => ({
        text: [m.title, m.verdict, m.signed_off_by].join(" ").toLowerCase(),
        fields: { verdict: [m.verdict], by: [m.signed_off_by] },
      })}
      storageKey="briefs"
      noun="briefs"
      empty={
        <p className="text-text/60">Nothing signed off yet. That's expected until a Staff+ reviewer signs off a map.</p>
      }
      card={(m) => (
        <Card className="h-full cursor-pointer hover:border-accent-line">
          <div className="mb-1 flex items-start justify-between gap-2">
            <h3 className="font-medium">{m.title}</h3>
            {m.promoted && <Badge tone="accent">promoted</Badge>}
          </div>
          <p className="text-xs text-text/50">{m.verdict}</p>
          <p className="mt-2 text-sm text-text/70">
            Signed off by {m.signed_off_by} on {m.signed_off_on}
          </p>
          <p className="mt-1 text-xs text-text/50">Catalog as of {ago(m.catalog_read_at ?? m.mapped_on)}</p>
        </Card>
      )}
      row={(m) => (
        <>
          <div className="truncate font-medium">{m.title}</div>
          <div className="text-xs text-text/50">
            {m.verdict} · signed off by {m.signed_off_by}
          </div>
        </>
      )}
      detail={(m) => <BriefDetail key={m.id} mapId={m.id} />}
    />
  );
}

/** A signed-off map reconciled with its review. Leads with who signed it off and when the
 * map read the catalog, then an amber "Stale" banner per registry that has changed since
 * (capabilities or standards this map references) — the guardrails may quote boundaries
 * that have moved. Boundaries to respect come before the capability list on purpose: they
 * are what a delivery team can't get anywhere else. Each capability's origin says whether
 * a reviewer confirmed it, added it, or never judged it. Only inferred standards are
 * listed here; always-on standards apply to every change. A markdown download carries the
 * same content into a ticket or design doc. */
function BriefDetail({ mapId }: { mapId: string }) {
  const state = useAsync(() => getGuardrails(mapId), [mapId]);
  if (state.status === "loading") return <p className="text-text/60">Loading…</p>;
  if (state.status === "error") return <p className="text-warn">{state.message}</p>;
  const d = state.data;

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-[family-name:--font-display] text-xl">{d.title}</h3>
          <p className="text-sm text-text/60">
            Signed off by {d.signed_off.by} on {d.signed_off.date} · verdict <em>{d.verdict}</em> · catalog as of{" "}
            {ago(d.catalog_read_at)}
          </p>
        </div>
        <DownloadMarkdown mapId={mapId} title={d.title} />
      </div>

      <MapFreshness mapId={mapId} showStale={false} />

      {d.staleness.map((s) => (
        <div key={s.registry} className="rounded border border-warn-line bg-warn-bg p-3 text-sm">
          <strong className="text-warn">Stale.</strong>{" "}
          {s.registry === "capabilities" ? (
            <CapabilityLinks ids={s.changed} />
          ) : s.registry === "standards" ? (
            s.changed.map((id, i) => (
              <span key={id}>
                {i > 0 && ", "}
                <StandardLink id={id} />
              </span>
            ))
          ) : (
            s.changed.join(", ")
          )}{" "}
          ({s.registry}) — {s.note}
        </div>
      ))}

      {d.boundaries.length > 0 && (
        <section>
          <h4 className="mb-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase">Boundaries to respect</h4>
          <ul className="flex flex-col gap-1.5 text-sm">
            {d.boundaries.map((b, i) => (
              <li key={i}>
                <strong>
                  <CapabilityLinks ids={b.between} separator=" ↔ " />
                </strong>{" "}
                — {b.resolution}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h4 className="mb-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase">Capabilities in scope</h4>
        <table className="w-full border-collapse text-left text-[13px]">
          <thead>
            <tr className="bg-sunken">
              <th className="px-2.5 py-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase">Capability</th>
              <th className="px-2.5 py-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase">Confidence</th>
              <th className="px-2.5 py-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase">Origin</th>
            </tr>
          </thead>
          <tbody>
            {d.capabilities.map((c) => (
              <tr key={c.capability} className="border-b border-line">
                <td className="px-2.5 py-1.5">
                  <CapabilityLink id={c.capability} />
                </td>
                <td className="px-2.5 py-1.5">{c.confidence}</td>
                <td className="px-2.5 py-1.5 text-text/60">
                  {c.origin === "added-in-review"
                    ? "added in review"
                    : c.confirmed
                      ? "confirmed"
                      : "not individually confirmed"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {(() => {
        const inferred = d.standards.filter((x) => x.trigger.reason === "inferred");
        return inferred.length > 0 ? (
          <section>
            <h4 className="mb-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase">
              Standards for this change
            </h4>
            <ul className="flex flex-col gap-1.5 text-sm">
              {inferred.map((x) => (
                <li key={x.id} className="flex items-center gap-2">
                  {x.tier === 0 && <Badge tone="warn">tier 0</Badge>}
                  <StandardLink id={x.id} className="font-medium">
                    {x.name}
                  </StandardLink>
                  <span className="text-text/50">— {x.trigger.because}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null;
      })()}

      {d.knock_on.length > 0 && (
        <section>
          <h4 className="mb-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase">Knock-on</h4>
          <ul className="flex flex-col gap-1 text-sm text-text/70">
            {d.knock_on.map((k, i) => (
              <li key={i}>
                <CapabilityLink id={k.from} /> shares <strong>{k.term}</strong> with <CapabilityLinks ids={k.reaches} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </Card>
  );
}

/** A file name from the map's title: "Pause at 90% budget" → "pause-at-90-budget.md". */
const fileName = (title: string) =>
  `${
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "delivery-brief"
  }.md`;

/** Downloads the brief as a markdown file. The markdown is fetched through the API client,
 * which sends the sign-in token, then saved from memory — a plain link to the API would be
 * refused (401) because a browser navigation carries no token. */
function DownloadMarkdown({ mapId, title }: { mapId: string; title: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    setError(null);
    setBusy(true);
    try {
      const markdown = await getGuardrailsMarkdown(mapId);
      const url = URL.createObjectURL(new Blob([markdown], { type: "text/markdown;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName(title);
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof ApiError ? e.errors.join("; ") : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button onClick={download} isDisabled={busy}>
        {busy ? "Downloading…" : "Download markdown"}
      </Button>
      {error && <p className="text-xs text-warn">{error}</p>}
    </div>
  );
}
